import { INSTAGRAM_AUTOMATION_STAGE_STATUSES } from './instagramAutomationStageStatuses'
import {
  InstagramRadioButtonType,
  generateRadioButtonStageDefs,
  waitForSectionElement,
} from './instagramRadioButtonUtils'
import { pushNotificationSettingsUrl } from './instagramUrls'
import { InstagramSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { InstagramRecommendationKey, StageType } from '../../utils/enums'
import { wait } from '../interruptiblePromises'
import {
  defaultCantUndoFn,
  getErrorIsDisabledAndReasonFn,
} from '../recommendationCardDisplayUtils'

enum OptionTypeA {
  OFF = 'Off',
  FROM_PROFILES_I_FOLLOW = 'From profiles I follow',
  FROM_EVERYONE = 'From everyone',
}

enum OptionTypeB {
  OFF = 'Off',
  ON = 'On',
}

enum OptionTypeC {
  OFF = 'Off',
  FROM_PRIMARY_ONLY = 'From primary only',
  FROM_PRIMARY_AND_GENERAL = 'From primary and general',
}

enum OptionType {
  A,
  B,
  C,
}

type OptionTypeEnum = OptionTypeA | OptionTypeB | OptionTypeC

const mapSettingToOptionType: Record<string, OptionType> = {
  [InstagramSettingKey.PUSH_NOTIFICATION_ACCOUNT_SUGGESTIONS]: OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_ADD_YOURS_PROMPTS]: OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_BIRTHDAYS]: OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_BROADCAST_CHANNEL_INVITES]:
    OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_BROADCAST_CHANNEL_MESSAGES]:
    OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_COMMENTS]: OptionType.A,
  [InstagramSettingKey.PUSH_NOTIFICATION_COMMENT_LIKES]: OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_COMMENT_DAILY_DIGEST]: OptionType.A,
  [InstagramSettingKey.PUSH_NOTIFICATION_FIRST_POSTS_AND_STORIES]: OptionType.A,
  [InstagramSettingKey.PUSH_NOTIFICATION_FUNDRAISERS_BY_OTHERS]: OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_GROUP_REQUESTS]: OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_LIKES]: OptionType.A,
  [InstagramSettingKey.PUSH_NOTIFICATION_LIKES_AND_COMMENTS_ON_PHOTOS_OF_YOU]:
    OptionType.A,
  [InstagramSettingKey.PUSH_NOTIFICATION_LIVE_VIDEOS]: OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_MENTIONS_IN_BIO]: OptionType.A,
  [InstagramSettingKey.PUSH_NOTIFICATION_MESSAGE_REQUESTS]: OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_MOST_WATCHED_REELS]: OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_NOTIFICATION_REMINDERS]: OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_ORIGINAL_AUDIO]: OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_PRODUCT_ANNOUNCEMENTS_AND_FEEDBACK]:
    OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_RECENTLY_UPLOADED_REELS]: OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_TRENDING_PLACES]: OptionType.B,
  [InstagramSettingKey.PUSH_NOTIFICATION_VIDEO_CHATS]: OptionType.A,
  [InstagramSettingKey.PUSH_NOTIFICATION_AI_GENERATED_CONTENT]: OptionType.B,
}

const mapSettingToTargetValue: Record<string, OptionTypeEnum> = {
  [InstagramSettingKey.PUSH_NOTIFICATION_ACCOUNT_SUGGESTIONS]: OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_ADD_YOURS_PROMPTS]: OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_BIRTHDAYS]: OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_BROADCAST_CHANNEL_INVITES]:
    OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_BROADCAST_CHANNEL_MESSAGES]:
    OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_COMMENTS]:
    OptionTypeA.FROM_PROFILES_I_FOLLOW,
  [InstagramSettingKey.PUSH_NOTIFICATION_COMMENT_LIKES]: OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_COMMENT_DAILY_DIGEST]: OptionTypeA.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_FIRST_POSTS_AND_STORIES]:
    OptionTypeA.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_FUNDRAISERS_BY_OTHERS]:
    OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_GROUP_REQUESTS]: OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_LIKES]:
    OptionTypeA.FROM_PROFILES_I_FOLLOW,
  [InstagramSettingKey.PUSH_NOTIFICATION_LIKES_AND_COMMENTS_ON_PHOTOS_OF_YOU]:
    OptionTypeA.FROM_PROFILES_I_FOLLOW,
  [InstagramSettingKey.PUSH_NOTIFICATION_LIVE_VIDEOS]: OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_MENTIONS_IN_BIO]:
    OptionTypeA.FROM_PROFILES_I_FOLLOW,
  [InstagramSettingKey.PUSH_NOTIFICATION_MESSAGE_REQUESTS]: OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_MOST_WATCHED_REELS]: OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_NOTIFICATION_REMINDERS]:
    OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_ORIGINAL_AUDIO]: OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_PRODUCT_ANNOUNCEMENTS_AND_FEEDBACK]:
    OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_RECENTLY_UPLOADED_REELS]:
    OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_TRENDING_PLACES]: OptionTypeB.OFF,
  [InstagramSettingKey.PUSH_NOTIFICATION_VIDEO_CHATS]:
    OptionTypeA.FROM_PROFILES_I_FOLLOW,
  [InstagramSettingKey.PUSH_NOTIFICATION_AI_GENERATED_CONTENT]: OptionTypeB.OFF,
}

const mapSettingToSectionLabel: Record<string, string> = {
  [InstagramSettingKey.PUSH_NOTIFICATION_ACCOUNT_SUGGESTIONS]:
    'Account suggestions',
  [InstagramSettingKey.PUSH_NOTIFICATION_ADD_YOURS_PROMPTS]: 'Add Yours',
  [InstagramSettingKey.PUSH_NOTIFICATION_BIRTHDAYS]: 'Birthdays',
  [InstagramSettingKey.PUSH_NOTIFICATION_BROADCAST_CHANNEL_INVITES]:
    'Broadcast channel invites',
  [InstagramSettingKey.PUSH_NOTIFICATION_BROADCAST_CHANNEL_MESSAGES]:
    'Broadcast channel messages',
  [InstagramSettingKey.PUSH_NOTIFICATION_COMMENTS]: 'Comments',
  [InstagramSettingKey.PUSH_NOTIFICATION_COMMENT_LIKES]: 'Comment likes',
  [InstagramSettingKey.PUSH_NOTIFICATION_COMMENT_DAILY_DIGEST]:
    'Comment daily digest',
  [InstagramSettingKey.PUSH_NOTIFICATION_FIRST_POSTS_AND_STORIES]:
    'First posts and stories',
  [InstagramSettingKey.PUSH_NOTIFICATION_FUNDRAISERS_BY_OTHERS]:
    'Fundraisers by others',
  [InstagramSettingKey.PUSH_NOTIFICATION_GROUP_REQUESTS]: 'Group requests',
  [InstagramSettingKey.PUSH_NOTIFICATION_LIKES]: 'Likes',
  [InstagramSettingKey.PUSH_NOTIFICATION_LIKES_AND_COMMENTS_ON_PHOTOS_OF_YOU]:
    'Likes and comments on photos of you',
  [InstagramSettingKey.PUSH_NOTIFICATION_LIVE_VIDEOS]: 'Live videos',
  [InstagramSettingKey.PUSH_NOTIFICATION_MENTIONS_IN_BIO]: 'Mentions in bio',
  [InstagramSettingKey.PUSH_NOTIFICATION_MESSAGE_REQUESTS]: 'Message requests',
  [InstagramSettingKey.PUSH_NOTIFICATION_MOST_WATCHED_REELS]:
    'Most watched reels',
  [InstagramSettingKey.PUSH_NOTIFICATION_NOTIFICATION_REMINDERS]: 'Reminders',
  [InstagramSettingKey.PUSH_NOTIFICATION_ORIGINAL_AUDIO]: 'Original audio',
  [InstagramSettingKey.PUSH_NOTIFICATION_PRODUCT_ANNOUNCEMENTS_AND_FEEDBACK]:
    'Product announcements & feedback',
  [InstagramSettingKey.PUSH_NOTIFICATION_RECENTLY_UPLOADED_REELS]:
    'Recently uploaded reels',
  [InstagramSettingKey.PUSH_NOTIFICATION_TRENDING_PLACES]: 'Trending places',
  [InstagramSettingKey.PUSH_NOTIFICATION_VIDEO_CHATS]: 'Video Chats',
  [InstagramSettingKey.PUSH_NOTIFICATION_AI_GENERATED_CONTENT]:
    'AI-generated posts',
}

function getShouldUpdate(
  currentValue: OptionTypeEnum,
  targetValue: OptionTypeEnum,
  settingKey: string,
): boolean {
  const optionType = mapSettingToOptionType[settingKey]

  let valueMap
  switch (optionType) {
    case OptionType.A:
      valueMap = {
        [OptionTypeA.FROM_EVERYONE]: 0,
        [OptionTypeA.FROM_PROFILES_I_FOLLOW]: 1,
        [OptionTypeA.OFF]: 2,
      }
      break
    case OptionType.B:
      valueMap = {
        [OptionTypeB.OFF]: 1,
        [OptionTypeB.ON]: 0,
      }
      break
    case OptionType.C:
      valueMap = {
        [OptionTypeC.FROM_PRIMARY_AND_GENERAL]: 0,
        [OptionTypeC.FROM_PRIMARY_ONLY]: 1,
        [OptionTypeC.OFF]: 2,
      }
      break
    default:
      throw new Error(`Did not recognize option type ${optionType}`)
  }

  return valueMap[currentValue] < valueMap[targetValue]
}

function getCurrentValueFn(
  settingsKey: string,
): (initialValues: { [key: string]: any }) => any {
  return (initialValues) => {
    const value = initialValues[settingsKey]

    if (value === undefined || value === null) {
      return null
    }

    return value
  }
}

function getIsFulfilledFn(
  settingsKey: string,
): (initialValues: { [key: string]: any }) => any {
  const targetValue = mapSettingToTargetValue[settingsKey]

  return (initialValues) => {
    const value = initialValues[settingsKey]

    if (value === undefined || value === null) {
      return false
    }

    return !getShouldUpdate(value, targetValue, settingsKey)
  }
}

const getCantUndoFn = () => defaultCantUndoFn

function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

// Automations

enum StageKey {
  GATHER_PUSH_NOTIFICATIONS = 'GATHER_PUSH_NOTIFICATIONS',
  UPDATE_PUSH_NOTIFICATIONS = 'UPDATE_PUSH_NOTIFICATIONS',
  UPDATE_PUSH_NOTIFICATIONS_WAIT = 'UPDATE_PUSH_NOTIFICATIONS_WAIT',
  UNDO_PUSH_NOTIFICATIONS = 'UNDO_PUSH_NOTIFICATIONS',
  UNDO_PUSH_NOTIFICATIONS_WAIT = 'UNDO_PUSH_NOTIFICATIONS_WAIT',
}

const { gatherStageDefs, updateStageDefs, undoStageDefs } =
  generateRadioButtonStageDefs(
    InstagramRecommendationKey.INSTAGRAM_PUSH_NOTIFICATIONS,
    Object.keys(mapSettingToSectionLabel),
    (x) => mapSettingToSectionLabel[x],
    (x) => mapSettingToTargetValue[x],
    getShouldUpdate,
    StageKey.GATHER_PUSH_NOTIFICATIONS,
    StageKey.UPDATE_PUSH_NOTIFICATIONS,
    StageKey.UNDO_PUSH_NOTIFICATIONS,
    pushNotificationSettingsUrl,
    InstagramRadioButtonType.Input,
  )

/**
 * Currently, email notifications page takes an extra second to load, and if
 * you try to click soon, the page stops working (doesn't register change in
 * setting, clicking other toggles doesn't work as expected).
 *
 * Until we find a more robust solution, we're just introducing a 2 second wait.
 */

updateStageDefs.unshift({
  type: StageType.ACTION,
  key: StageKey.UPDATE_PUSH_NOTIFICATIONS_WAIT,
  status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
  url: pushNotificationSettingsUrl,
  action: async () => {
    await waitForSectionElement(
      mapSettingToSectionLabel[InstagramSettingKey.PUSH_NOTIFICATION_LIKES],
      InstagramRadioButtonType.Input,
    )
    await wait(2000)
  },
})

undoStageDefs.unshift({
  type: StageType.ACTION,
  key: StageKey.UNDO_PUSH_NOTIFICATIONS_WAIT,
  status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
  url: pushNotificationSettingsUrl,
  action: async () => {
    await waitForSectionElement(
      mapSettingToSectionLabel[InstagramSettingKey.PUSH_NOTIFICATION_LIKES],
      InstagramRadioButtonType.Input,
    )
    await wait(2000)
  },
})

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
